import * as React from 'react';
import { ComplexValue, MeatballMenu } from 'ui';
import { FaInfo } from 'react-icons/fa';
import {
  readSelectionParams, simpleFormNames, mapToStore, DefaultValue, selectDefaultValue,
  updateDefaultValue, getUniqueFields, getCurrentFieldId
} from './panel-default-values-utils';
import { withRoleBasedAccess } from '../role-based-access';
import {
  PanelDefaultValuesComponentProps, PanelDefaultValuesProps,
  PanelDefaultValuesState
} from 'app/components/access-panel/default-values/types';
import { sfParameters } from 'app/components/access-panel/sf-names';
import { AddDefaultValue } from 'app/components/access-panel/default-values/add-default-value';
import { CardValue } from 'app/components/access-panel/default-values/card-value';

import styles from './panel-default-values.scss';
import { getDuplicatedValueFields } from 'app/components/access-panel/default-values/get-duplicated-value-fields';
import { getDefaultValueFields } from 'app/components/access-panel/default-values/get-default-value-fields';
import { sortDefaultValues } from 'app/components/access-panel/default-values/sort-default-values';

class PanelDefaultValuesComponent extends React.Component<PanelDefaultValuesComponentProps> {

  public componentDidMount() {
    this.buildInitalState();
  }

  public componentDidUpdate(prevProps: PanelDefaultValuesComponentProps) {

    this.buildInitalState();

    const selections = readSelectionParams(this.props.formValues);
    const prevSelections = readSelectionParams(prevProps.formValues);

    if (selections.data.optionsColection && prevSelections.data.optionsColection
      && selections.data.optionsColection.value !== prevSelections.data.optionsColection.value) {
      this.props.clearValueInStore(simpleFormNames.values);
      this.props.clearValueInStore(simpleFormNames.valuesFields);
    }
    if (selections.data.caseType && prevSelections.data.caseType
      && selections.data.caseType.value !== prevSelections.data.caseType.value) {
      this.props.clearValueInStore(simpleFormNames.values);
      this.props.clearValueInStore(simpleFormNames.valuesFields);
    }
  }

  private buildInitalState = () => {

    const { customState, changeCustomState, initialValue } = this.props;

    // Check if state does not exist and there is initial value
    if (customState || !initialValue) {
      return;
    }

    changeCustomState({
      values: mapToStore(initialValue)
    });
  }

  private handleClickAddButton = () => {
    const { valid, data } = readSelectionParams(this.props.formValues);
    const { customState: { values = [] } = {}, changeCustomState } = this.props;

    if (!valid) {
      return;
    }

    const existingGroup = selectDefaultValue(values, data);
    const uniqueFields = getUniqueFields(values, data);

    changeCustomState({
      values: updateDefaultValue(
        values,
        data,
        [
          ...(existingGroup ? existingGroup.defaultValueFields : []),
          ...uniqueFields
        ]
      )
    });
  }

  private removeGroup = (defaultValue: DefaultValue) => {

    const { customState: { values = [] } = {},
      changeCustomState, clearValidation } = this.props;
    const existingGroup = selectDefaultValue(values, defaultValue);

    if (!existingGroup) {
      return;
    }

    clearValidation(this.props.formName, {
      [sfParameters.caseType]: existingGroup.caseType.value,
      [sfParameters.defaultValue]: existingGroup.defaultValue.value
    });

    changeCustomState({
      values: values.filter(d => d !== existingGroup)
    });
  }

  private onChange = (value: ComplexValue<number>[], defaultValue: DefaultValue) => {

    const { customState: { values = [] } = {},
      changeCustomState, clearValidation } = this.props;
    const existingGroup = selectDefaultValue(values, defaultValue);
    const currentFieldId = getCurrentFieldId(
      defaultValue.defaultValueFields.map(d => d.value),
      value.map(v => v.value)
    );

    if (!existingGroup) {
      return;
    }

    clearValidation(this.props.formName, {
      [sfParameters.caseType]: existingGroup.caseType.value,
      [sfParameters.field]: currentFieldId
    });

    changeCustomState({
      values: updateDefaultValue(
        values,
        defaultValue,
        value
      )
    });
  }

  private removeAll = () => {
    this.props.changeCustomState({
      ...this.props.customState,
      values: []
    });
  }

  private initialDefaultValueFields = () => {
    const { formValues = {}, customState: { values: customStateValues = [] } = {} } = this.props;
    return getDefaultValueFields(customStateValues, formValues);
  }

  private duplicatedValueFields = () => {
    const initialDefaultValueFields = this.initialDefaultValueFields();
    const { formValues = {}, customState: { values: customStateValues = [] } = {} } = this.props;
    return getDuplicatedValueFields(customStateValues, formValues, initialDefaultValueFields);
  }

  private hasDuplicatedValueFields = () => {
    const duplicatedValueFields = this.duplicatedValueFields();
    return !!duplicatedValueFields && duplicatedValueFields.length > 0;
  }

  private renderValidationErrors = () => {
    return this.hasDuplicatedValueFields() && <div className={styles.errorPanel}>
      <p><FaInfo /> {this.props.translation.add_default_value_duplicated_values}</p>
      <ul>{this.duplicatedValueFields().map((d) => <li key={d.value}>{d.description}</li>)}</ul>
    </div>;
  }

  public render(): JSX.Element {
    const { translation, readOnly } = this.props;
    const { customState = { values: [] } } = this.props;

    return (<>
      {!readOnly &&
        <div className={styles.top}>
          <div className="float-left">
            <AddDefaultValue
              onSubmit={this.handleClickAddButton}
              initialDefaultValueFields={this.initialDefaultValueFields()}
              validationErrors={this.hasDuplicatedValueFields && this.renderValidationErrors()}
              {...this.props} />
          </div>
          {customState.values.length > 0 && <div className={`${styles.meatball} float-right`}>
            <MeatballMenu className={styles.meatball} closeAfterClick={true}>
              <li>
                <button type="button" className="dropdown-item" onClick={this.removeAll}>
                  {translation.remove_all_section}
                </button>
              </li>
            </MeatballMenu>
          </div>}
        </div>}
      {customState.values && !!customState.values.length
        ? <div className="row">
          {sortDefaultValues(customState.values).map((item, index) => <CardValue
            key={index}
            defaultValue={item}
            onChange={this.onChange}
            removeGroup={this.removeGroup}
            handleClickAddButton={this.handleClickAddButton}
            validationErrors={this.hasDuplicatedValueFields && this.renderValidationErrors()}
            {...this.props} />)}
        </div>
        : !!readOnly && <p className="no-content">{translation.no_access_has_been_granted}</p>}
    </>);
  }
}

export const PanelDefaultValues = withRoleBasedAccess<PanelDefaultValuesProps, PanelDefaultValuesState>(PanelDefaultValuesComponent);
